import React, { useEffect, useCallback, useRef } from 'react';
import { VibesitesImage } from '@vibesites/ui-primitives';

/**
 * Shared building blocks for the Gallery section(s). Not a registered
 * component — has no `contentSchema`, never appears in `registry.ts`, and
 * is not re-exported from `components/index.ts`. The schema linter skips
 * files without a contentSchema, so this helper sits safely alongside the
 * registered gallery section without tripping any rules (every photo is
 * rendered through VibesitesImage).
 */

export interface GalleryImage {
  image: string;
  alt_text?: string;
  title?: string;
  description?: string;
  source_page_urls?: string;
  original_url?: string;
}

/** Classify a gallery photo by its title — used to filter apartment vs community shots. */
export function classifyPhoto(photo: GalleryImage): 'apartment' | 'community' | 'other' {
  const title = (photo.title || '').toLowerCase().trim();
  if (title.includes('apartment')) return 'apartment';
  if (title.includes('community')) return 'community';
  return 'other';
}

interface GalleryLightboxProps {
  images: GalleryImage[];
  selectedIndex: number | null;
  onClose: () => void;
  onChange: (index: number | null) => void;
}

/**
 * Full-screen slideshow lightbox. Click a tile in the parent gallery, the
 * parent sets `selectedIndex`, this component renders an overlay with:
 *  - prev/next arrows
 *  - keyboard navigation (← →, Esc)
 *  - mobile swipe (horizontal-only, ignores vertical scrolls)
 *  - body scroll lock while open
 *  - looping index (wraps past first/last)
 *
 * Styles are embedded so the component is self-contained — no separate
 * GallerySharedStyles import required.
 */
export function GalleryLightbox({
  images,
  selectedIndex,
  onClose,
  onChange,
}: GalleryLightboxProps) {
  const total = images.length;

  const goNext = useCallback(
    (e?: React.MouseEvent | React.KeyboardEvent) => {
      e?.stopPropagation();
      onChange(selectedIndex === null ? null : (selectedIndex + 1) % total);
    },
    [onChange, selectedIndex, total],
  );

  const goPrev = useCallback(
    (e?: React.MouseEvent | React.KeyboardEvent) => {
      e?.stopPropagation();
      onChange(selectedIndex === null ? null : (selectedIndex - 1 + total) % total);
    },
    [onChange, selectedIndex, total],
  );

  // Keyboard navigation
  useEffect(() => {
    if (selectedIndex === null) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') goNext();
      else if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [selectedIndex, goNext, goPrev, onClose]);

  // Lock body scroll while open so swipes don't drag the page underneath
  // on mobile.
  useEffect(() => {
    if (selectedIndex === null) return;
    if (typeof document === 'undefined') return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [selectedIndex]);

  // Touch / swipe state — track both axes so vertical scrolls don't get
  // mis-interpreted as horizontal slideshow advances.
  const touchStart = useRef<{ x: number; y: number } | null>(null);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const t = e.touches[0];
    touchStart.current = { x: t.clientX, y: t.clientY };
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      const start = touchStart.current;
      touchStart.current = null;
      if (!start) return;
      const t = e.changedTouches[0];
      const dx = t.clientX - start.x;
      const dy = t.clientY - start.y;
      // Ignore tiny taps and gestures that are more vertical than horizontal.
      if (Math.abs(dx) < 40) return;
      if (Math.abs(dy) > Math.abs(dx)) return;
      if (dx < 0) goNext();
      else goPrev();
    },
    [goNext, goPrev],
  );

  const selectedImage = selectedIndex !== null ? images[selectedIndex] : null;
  if (!selectedImage) return null;

  return (
    <div
      className="gallery-lightbox"
      onClick={onClose}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      role="dialog"
      aria-modal="true"
      aria-label="Image lightbox"
    >
      <button
        onClick={onClose}
        className="gallery-lightbox__close"
        aria-label="Close lightbox"
      >
        &times;
      </button>

      {total > 1 && (
        <button
          onClick={goPrev}
          className="gallery-lightbox__arrow gallery-lightbox__arrow--prev"
          aria-label="Previous image"
        >
          &#8249;
        </button>
      )}

      <div className="gallery-lightbox__img-wrap" onClick={(e) => e.stopPropagation()}>
        <VibesitesImage
          url={selectedImage.image || selectedImage.original_url || ''}
          alt={selectedImage.alt_text || selectedImage.title || ''}
          className="gallery-lightbox__img"
        />
      </div>

      {total > 1 && (
        <button
          onClick={goNext}
          className="gallery-lightbox__arrow gallery-lightbox__arrow--next"
          aria-label="Next image"
        >
          &#8250;
        </button>
      )}

      {total > 1 && (
        <div className="gallery-lightbox__counter" aria-live="polite">
          {(selectedIndex ?? 0) + 1} / {total}
        </div>
      )}

      <style>{`
        .gallery-lightbox {
          position: fixed;
          inset: 0;
          z-index: 50;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(0, 0, 0, 0.9);
          padding: 1rem;
          touch-action: pan-y;
          animation: gallery-lightbox-fade-in 0.2s ease both;
        }

        @keyframes gallery-lightbox-fade-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }

        .gallery-lightbox__img-wrap {
          display: flex;
          align-items: center;
          justify-content: center;
          max-width: calc(100vw - 8rem);
          max-height: 90vh;
        }

        .gallery-lightbox__img {
          max-width: 100%;
          max-height: 90vh;
          object-fit: contain;
          display: block;
        }

        .gallery-lightbox__close {
          position: absolute;
          top: 1rem;
          right: 1rem;
          color: #fff;
          font-size: 2.25rem;
          line-height: 1;
          background: none;
          border: none;
          cursor: pointer;
          opacity: 0.85;
          z-index: 10;
          padding: 0.25rem 0.5rem;
          transition: opacity 0.15s;
        }
        .gallery-lightbox__close:hover { opacity: 1; }

        .gallery-lightbox__arrow {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          color: #fff;
          font-size: 4rem;
          line-height: 1;
          background: rgba(0, 0, 0, 0.35);
          border: none;
          cursor: pointer;
          padding: 0.25rem 0.75rem;
          border-radius: 4px;
          opacity: 0.8;
          z-index: 10;
          transition: opacity 0.15s, background 0.15s;
          -webkit-user-select: none;
          user-select: none;
        }
        .gallery-lightbox__arrow:hover {
          opacity: 1;
          background: rgba(0, 0, 0, 0.6);
        }
        .gallery-lightbox__arrow--prev { left: 0.75rem; }
        .gallery-lightbox__arrow--next { right: 0.75rem; }

        .gallery-lightbox__counter {
          position: absolute;
          bottom: 1.25rem;
          left: 50%;
          transform: translateX(-50%);
          color: rgba(255, 255, 255, 0.75);
          font-size: 0.875rem;
          letter-spacing: 0.05em;
          pointer-events: none;
        }

        @media (max-width: 639px) {
          .gallery-lightbox__img-wrap {
            max-width: 100vw;
          }
          .gallery-lightbox__arrow {
            font-size: 2.75rem;
            padding: 0.15rem 0.5rem;
          }
          .gallery-lightbox__arrow--prev { left: 0.25rem; }
          .gallery-lightbox__arrow--next { right: 0.25rem; }
        }
      `}</style>
    </div>
  );
}
